import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from './../../environments/environment';
import { User, Friend, Follow } from './../models/user';
import { StorageService } from './storage.service'
import { AlertController } from '@ionic/angular';
import { NetworkService } from './network.service';
import { IndexedDbService } from './indexed-db.service';
import { blobToBase64 } from '../utils'
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  user: User | null = null; //User actuellement connecté
  friends: Friend[] = [];
  follows: Follow[] = [];

  constructor(
      private http: HttpClient,
      public storageService: StorageService,
      public networkService: NetworkService,
      private translate: TranslateService,
      public indexedDbService: IndexedDbService,
      public alertController: AlertController) { }

  dbSave(user: User, passwordNew: string | null, isCreateOrganization: boolean = false, organizationType: string | null = null, organizationName: string | null = null) {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData["email"] = user.email;
        formData["password"] = passwordNew;
        formData["lastName"] = user.lastName;
        formData["firstName"] = user.firstName;
        formData["phoneNumber"] = user.phoneNumber;
        formData["addrStreet"] = user.addrStreet;
        formData["addrPostalcode"] = user.addrPostalcode;
        formData["addrCity"] = user.addrCity;
        formData["addrCountry"] = user.addrCountry;
        formData["birthday"] = user.birthday;
        formData["gender"] = user.gender;
        formData["publicPageIsActive"] = user.publicPageIsActive;
        formData["postDefaultVisiblity"] = user.postDefaultVisiblity;
        formData["isVisibleOnSearch"] = user.isVisibleOnSearch;
        formData["platform"] = Capacitor.getPlatform();

        formData["isCreateOrganization"] = isCreateOrganization;
        formData["organizationType"] = organizationType;
        formData["organizationName"] = organizationName;
        
        if(user.avatarNewFileName != null) {
          formData["avatar"] = {'base64': user.avatarBase64, 'name': user.avatarNewFileName}
        }

        var url = environment.API_URL + 'userProfile/ws_private/UpdateProfil/';
        if(user.id <= 0)
          url = environment.API_URL + 'userProfile/ws_public/CreateAccount/'
        this.http.post<any[]>(url, formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            if (user.id <= 0) {
              user.id = response['id']
              user.token = response['token']['key']
            }
            resolve(user);
          },
          error: async (error) => {
            if (error.status === 400 && error.error.error == 'EmailAlreadyUsed') {
              var alert = await this.alertController.create({
                header: this.translate.instant("Warning"),
                subHeader: this.translate.instant("Unable to save"),
                message: this.translate.instant("Un compte utilisateur avec cette adresse email existe déjà, vous ne pouvez pas en créer un second. Si vous avez perdu le mot de passe de votre compte, vous pouvez en générer un nouveau avec le formulaire mot de passe perdu"),
                buttons: ['OK'],
              });
              await alert.present();
              reject(error);
            } else {
              var alert = await this.alertController.create({
                header: this.translate.instant("Warning"),
                subHeader: this.translate.instant("Unable to save"),
                message: this.translate.instant("An error occurred, the backup failed."),
                buttons: ['OK'],
              });
              await alert.present();
              reject(error);
            }
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': user, 'action': 'save'}).subscribe(() => {});
        resolve(user)
      }
    });
  }

  dbLoadProfile(user: User): Promise<User | null> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'userProfile/ws_private/GetProfil/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          user.id = response['id']
          user.firstName = response['firstName']
          user.lastName = response['lastName']
          user.email = response['email']
          user.birthday = response['birthday']
          user.avatar = response['avatar']
          user.gender = response['gender']
          user.addrStreet = response['addrStreet']
          user.addrPostalcode = response['addrPostalcode']
          user.addrCity = response['addrCity']
          user.addrCountry = response['addrCountry']
          user.phoneNumber = response['phoneNumber']
          user.identifier = response['identifier']
          user.publicPageIsActive = response['publicPageIsActive']
          user.postDefaultVisiblity = response['postDefaultVisiblity']
          user.isVisibleOnSearch = response['isVisibleOnSearch']

          user.avatarBase64 = null;
          if(user.avatar != null) {
            this.http.get(response['avatar'],  { responseType: 'blob' as 'blob' }).subscribe(data => {
              blobToBase64(data).then((res: any) =>  {
                if(user)
                  user.avatarBase64 = res;
                resolve(user);
              });
            },
            () => {
              resolve(user);
            });
          } else {
            resolve(user);
          }
        },
        () => resolve(user))
      } else {
        resolve(user);
      }
    });
  }

  getHttpHeaders(): string {
    if(this.user?.token) {
      return "Token " + this.user.token
    };

    return '';
  }


  dbGetFriendsAndFollowsOfUser(user: User): Promise<{ friends: Friend[], follows: Follow[] }> {
    return new Promise((resolve) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
          'Authorization': this.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'userProfile/ws_private/' + user.id + '/GetFriendsAndFollowsOfUser/', {headers : headers}).subscribe(async (response: { [key: string]: any }) => {
          let friends: Friend[] = [];
          let follows: Follow[] = [];
          let avatars: { [key: string]: string } = {};

          for(let d of response['friends']) {
            let f = this.friends.find(f => f.id == d['id']);
            if(!f) {
              f = new Friend();
              f.id = d['id'];
              this.friends.push(f);
            }
            f.isConfirmedByFriend = d['isConfirmedByFriend'];

            f.user = new User();
            f.user.id = d['user']['id'];
            f.user.firstName = d['user']['firstName'];
            f.user.lastName = d['user']['lastName'];
            f.user.avatar = d['user']['avatar'];
            if(d['user']['avatarBase64']) {
              f.user.avatarBase64 = d['user']['avatarBase64'];
              avatars[d['user']['id']] = d['user']['avatarBase64']
            } else {
              if(avatars[d['user']['id']]) {
                f.user.avatarBase64 = avatars[d['user']['id']]
              }
            }
            f.user.identifier = d['user']['identifier'];

            f.friend = new User();
            f.friend.id = d['friend']['id'];
            f.friend.firstName = d['friend']['firstName'];
            f.friend.lastName = d['friend']['lastName'];
            f.friend.avatar = d['friend']['avatar'];
            if(d['friend']['avatarBase64']) {
              f.friend.avatarBase64 = d['friend']['avatarBase64'];
              avatars[d['friend']['id']] = d['friend']['avatarBase64']
            } else {
              if(avatars[d['friend']['id']]) {
                f.friend.avatarBase64 = avatars[d['friend']['id']]
              }
            }
            f.friend.identifier = d['friend']['identifier'];

            friends.push(f);
          }
          for(let d of response['follows']) {
            let f = this.follows.find(f => f.id == d['id']);
            if(!f) {
              f = new Follow();
              f.id = d['id'];
              this.follows.push(f);
            }
            f.isNotif = d['isNotif'];
            f.user = new User();
            f.user.id = user.id;
            f.user.firstName = user.firstName;
            f.user.lastName = user.lastName;
            f.user.avatar = user.avatar;
            f.user.avatarBase64 = user.avatarBase64;
            f.user.identifier = user.identifier;

            f.follow = new User();
            f.follow.id = d['follow']['id'];
            f.follow.firstName = d['follow']['firstName'];
            f.follow.lastName = d['follow']['lastName'];
            f.follow.avatar = d['follow']['avatar'];
            if(d['follow']['avatarBase64']) {
              f.follow.avatarBase64 = d['follow']['avatarBase64'];
              avatars[d['follow']['id']] = d['follow']['avatarBase64']
            } else {
              if(avatars[d['follow']['id']]) {
                f.follow.avatarBase64 = avatars[d['follow']['id']]
              }
            }
            f.follow.identifier = d['follow']['identifier'];
            follows.push(f);
          }

          for(let f of friends) {
            f.friendIsFollowed = follows.some(follow => follow.follow?.id == f.friend?.id);
          }

          resolve({ friends: friends, follows: follows });
        },
        () => resolve({ friends: this.friends.filter(f => f.friend?.id == user.id || f.user?.id == user.id), follows: this.follows.filter(f => f.user?.id == user.id) }));
      } else {
        resolve({ friends: this.friends.filter(f => f.friend?.id == user.id || f.user?.id == user.id), follows: this.follows.filter(f => f.user?.id == user.id) });
      }
    });
  }

  askFriend(friend: Friend): Promise<Friend> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.getHttpHeaders()
        });

        this.http.get<any[]>(environment.API_URL + 'userProfile/ws_private/' + friend.friend?.id + '/AskFriend/', {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            friend.id = response['id']
            resolve(friend);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': friend, 'action': 'askFriend'}).subscribe(() => {});
        resolve(friend)
      }
    });
  }

  cancelFriendRequest(friend: Friend): Promise<Friend> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.getHttpHeaders()
        });

        this.http.get<any[]>(environment.API_URL + 'userProfile/ws_private/' + friend.id + '/DeleteFriend/', {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            this.friends = this.friends.filter(f => f.id != friend.id)
            resolve(friend);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': friend, 'action': 'askFriend'}).subscribe(() => {});
        resolve(friend)
      }
    });
  }

  acceptFriendRequest(friend: Friend): Promise<Friend> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
            'Authorization': this.getHttpHeaders()
        });

        this.http.get<any[]>(environment.API_URL + 'userProfile/ws_private/' + friend.id + '/AcceptFriend/', {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            friend.isConfirmedByFriend = true;

            let follow = this.follows.find(f => f.user?.id == friend.user?.id && f.follow?.id == friend.friend?.id)
            if(follow) {
              follow.followIsFriend = true
            }

            resolve(friend);
          },
          error: async (error) => {
            reject(error);
          }
        });
      } else {
        this.indexedDbService.addItem({'obj': friend, 'action': 'askFriend'}).subscribe(() => {});
        resolve(friend)
      }
    });
  }

  updateFollow(follow: Follow): Promise<Follow> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
          'Authorization': this.getHttpHeaders()
        });
        let formData: { [key: string]: any } = {};
        formData['id'] = follow.id;
        formData["userId"] = follow.user?.id;
        formData["followUserId"] = follow.follow?.id;
        formData["isNotif"] = follow.isNotif;

        this.http.post<any[]>(environment.API_URL + 'userProfile/ws_private/UpdateFollow/', formData, {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            if(follow.id == null) {
              follow.id = response['id']
              this.follows.push(follow)
            }

            follow.followIsFriend = false
            let friend = this.friends.find(f => f.user?.id == follow.user?.id && f.friend?.id == follow.follow?.id)
            if(friend) {
              friend.friendIsFollowed = true
              follow.followIsFriend = true
            }
            
            resolve(follow);
          },
          error: async (error) => {
            reject(error);
          }
        });
      }
    });
  }

  deleteFollow(follow: Follow): Promise<Follow> {
    return new Promise((resolve, reject) => {
      if (this.networkService.isOnline()) {
        let headers = new HttpHeaders({
          'Authorization': this.getHttpHeaders()
        });
        this.http.get<any[]>(environment.API_URL + 'userProfile/ws_private/' + follow.id + '/DeleteFollow/', {headers : headers}).subscribe({
          next: (response: { [key: string]: any }) => {
            this.follows = this.follows.filter(f => f.id != follow.id)

            let friend = this.friends.find(f => f.user?.id == follow.user?.id && f.friend?.id == follow.follow?.id)
            if(friend) {
              friend.friendIsFollowed = false
            }

            resolve(follow);
          },
          error: async (error) => {
            reject(error);
          }
        });
      }
    });
  }
}
